import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, FileText, Plus } from "lucide-react";
import { api, formatApiErrorDetail } from "../../api";
import { Card, EmptyState, PageHeader, formatDate } from "../../ui";
import { DateInput, todayISO } from "../../DateInput";
import { Field, STATUS_LABEL, Spinner, StatusBadge, downloadBlob, fmtBRL } from "./shared";
import { MarkPaidModal } from "./MarkPaidModal";

const HeaderActions = ({ onExport }) => (
  <div className="flex flex-wrap items-center gap-2">
    <Link
      to="/admin/despesas/novo"
      data-testid="admin-expenses-new"
      className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#002776] text-white text-[11px] uppercase tracking-[0.25em] hover:bg-[#001a5a]"
    >
      <Plus size={14} /> Novo relatório
    </Link>
    <button
      data-testid="admin-expenses-export"
      onClick={onExport}
      className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#002776] text-[#002776] text-[11px] uppercase tracking-[0.25em] hover:bg-[#002776] hover:text-white"
    >
      <Download size={14} /> Exportar XLSX
    </button>
  </div>
);

const Filters = ({ filters, setFilters }) => {
  const set = (key) => (v) => setFilters((f) => ({ ...f, [key]: v }));
  return (
    <Card className="mt-6 p-5">
      <div className="grid md:grid-cols-4 gap-3">
        <Field label="Status">
          <select
            data-testid="admin-expenses-filter-status"
            value={filters.status}
            onChange={(e) => set("status")(e.target.value)}
            className="mt-1 w-full border border-slate-300 px-3 py-2 rounded-sm bg-white"
          >
            <option value="">Todos</option>
            {Object.entries(STATUS_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Data inicial">
          <DateInput value={filters.start_date} onChange={set("start_date")} testid="admin-expenses-filter-start" />
        </Field>
        <Field label="Data final">
          <DateInput value={filters.end_date} onChange={set("end_date")} testid="admin-expenses-filter-end" />
        </Field>
      </div>
    </Card>
  );
};

const AdminRow = ({ r, onPay }) => (
  <tr className="border-t border-slate-100 hover:bg-slate-50" data-testid={`admin-expense-row-${r.id}`}>
    <td className="px-3 py-2 font-medium">
      <Link to={`/admin/despesas/${r.id}`} className="text-[#002776] hover:underline">
        {r.title}
      </Link>
    </td>
    <td className="px-3 py-2 text-slate-600">{r.author_name}</td>
    <td className="px-3 py-2 text-xs text-slate-600">
      {formatDate(r.start_date)} — {formatDate(r.end_date)}
    </td>
    <td className="px-3 py-2 text-right">{fmtBRL(r.total)}</td>
    <td className="px-3 py-2">
      <StatusBadge status={r.status} />
    </td>
    <td className="px-3 py-2 text-right space-x-2">
      {r.status === "aprovado" && (
        <button
          data-testid={`admin-expense-pay-${r.id}`}
          onClick={() => onPay(r)}
          className="inline-flex items-center gap-1 px-2 py-1 border border-[#002776] text-[#002776] text-[10px] uppercase tracking-wide hover:bg-[#002776] hover:text-white transition-colors"
        >
          <FileText size={12} /> Marcar pago
        </button>
      )}
    </td>
  </tr>
);

const AdminTable = ({ rows, onPay }) => (
  <table className="min-w-full text-sm" data-testid="admin-expenses-table">
    <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-600">
      <tr>
        <th className="text-left px-3 py-2">Título</th>
        <th className="text-left px-3 py-2">Autor</th>
        <th className="text-left px-3 py-2">Período</th>
        <th className="text-right px-3 py-2">Total</th>
        <th className="text-left px-3 py-2">Status</th>
        <th className="text-right px-3 py-2">Ações</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <AdminRow key={r.id} r={r} onPay={onPay} />
      ))}
    </tbody>
  </table>
);

export function AdminExpensesList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: "", author_id: "", start_date: "", end_date: "" });
  const [payTarget, setPayTarget] = useState(null);

  const params = useMemo(
    () => Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
    [filters]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/despesas", { params });
      setRows(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const exportXlsx = async () => {
    try {
      const res = await api.get("/admin/despesas/export", { params, responseType: "blob" });
      downloadBlob(res.data, `restituicao_despesas_${todayISO()}.xlsx`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="admin-expenses-page">
      <PageHeader
        title="Restituição de Despesas"
        subtitle="Visão administrativa de todos os relatórios; anexe comprovante de pagamento após a aprovação para encerrar o ciclo."
        actions={<HeaderActions onExport={exportXlsx} />}
      />
      <Filters filters={filters} setFilters={setFilters} />
      <Card className="mt-4 p-0 overflow-hidden">
        {loading ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <EmptyState title="Nenhum relatório encontrado." />
        ) : (
          <AdminTable rows={rows} onPay={setPayTarget} />
        )}
      </Card>
      {payTarget && (
        <MarkPaidModal
          row={payTarget}
          onClose={() => setPayTarget(null)}
          onSaved={() => {
            setPayTarget(null);
            load();
          }}
        />
      )}
    </div>
  );
}
